import React, { useState, useEffect, useCallback } from "react";
import {
  Card, Avatar, Button, Input, Upload, Typography,
  Space, message, Modal, Tag, Tabs, Spin, Select
} from "antd";
import Layout from "../../components/layout/Layout";
import {
  SendOutlined, PictureOutlined, HeartOutlined, MessageOutlined,
  RetweetOutlined, ClockCircleOutlined, FireOutlined,
  UserOutlined, GlobalOutlined, StarOutlined, UserAddOutlined,
  LoadingOutlined
} from "@ant-design/icons";
import { motion } from "framer-motion";
import ReactMarkdown from "react-markdown";
import { useAuth } from "../../contexts/AuthContext";
import squareService from "../../services/squareService";
import authService from "../../services/authService";
import "./SquarePage.css";

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const SquarePage = () => {
  const { isAuthenticated, user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [newPost, setNewPost] = useState("");
  const [selectedImage, setSelectedImage] = useState(null);
  const [sortBy, setSortBy] = useState("time"); // "time" or "hot"
  const [timeline, setTimeline] = useState("discover"); // "discover", "following", or "bookmarks"
  const [replyModalVisible, setReplyModalVisible] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [stats, setStats] = useState({});
  const [expandedReplies, setExpandedReplies] = useState({});
  const [followedUsers, setFollowedUsers] = useState(new Set());
  const [bookmarkedPosts, setBookmarkedPosts] = useState(new Set());
  const [likedPosts, setLikedPosts] = useState(new Set());

  // 🆕 加载状态管理
  const [loading, setLoading] = useState(false);
  const [postsLoading, setPostsLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    has_more: false
  });

  // 🆕 标签和搜索相关状态
  const [postTags, setPostTags] = useState([]); // 当前帖子的标签
  const [popularTags, setPopularTags] = useState([]); // 热门标签
  const [searchQuery, setSearchQuery] = useState(""); // 搜索关键词
  const [searchResults, setSearchResults] = useState([]); // 搜索结果
  const [isSearchMode, setIsSearchMode] = useState(false); // 是否在搜索模式
  const [searchLoading, setSearchLoading] = useState(false); // 搜索加载状态
  const [searchSort, setSearchSort] = useState("time"); // 搜索排序方式
  const [bookmarkedPostsList, setBookmarkedPostsList] = useState([]); // 收藏的帖子列表

  // 🆕 用户头像状态
  const [currentUserAvatar, setCurrentUserAvatar] = useState(null);

  // 🆕 加载帖子数据
  const loadPosts = useCallback(async (page = 1, resetPosts = false) => {
    try {
      setPostsLoading(true);
      const response = await squareService.getPosts({
        timeline,
        sort: sortBy,
        page,
        limit: 20
      });

      if (response.success) {
        const newPosts = response.data.posts || [];

        // 更新帖子状态（点赞、收藏等）
        newPosts.forEach(post => {
          if (post.is_liked) likedPosts.add(post.id);
          if (post.is_bookmarked) bookmarkedPosts.add(post.id);
        });

        if (resetPosts || page === 1) {
          setPosts(newPosts);
        } else {
          setPosts(prev => [...prev, ...newPosts]);
        }

        setPagination(response.data.pagination || {});
      } else {
        message.error('Failed to load posts');
      }
    } catch (error) {
      console.error('Error loading posts:', error);
      message.error('Failed to load posts');
    } finally {
      setPostsLoading(false);
    }
  }, [timeline, sortBy, likedPosts, bookmarkedPosts]);

  // 🆕 加载统计数据
  const loadStats = async () => {
    try {
      setStatsLoading(true);
      const response = await squareService.getStats();

      if (response.success) {
        setStats(response.data);
      } else {
        console.error('Failed to load stats');
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setStatsLoading(false);
    }
  };

  // 🆕 加载热门标签
  const loadPopularTags = async () => {
    try {
      const response = await squareService.getPopularTags(20);

      if (response.success) {
        setPopularTags(response.data.tags || []);
      } else {
        console.error('Failed to load popular tags');
      }
    } catch (error) {
      console.error('Error loading popular tags:', error);
    }
  };

  // 🆕 搜索帖子
  const handleSearch = async (query, sort = "time") => {
    if (!query || !query.trim()) {
      setIsSearchMode(false);
      setSearchResults([]);
      return;
    }

    try {
      setSearchLoading(true);
      setIsSearchMode(true);

      const response = await squareService.searchPosts({
        q: query.trim(),
        sort: sort,
        page: 1,
        limit: 20
      });

      if (response.success) {
        setSearchResults(response.data.posts || []);
        setSearchQuery(query);
        setSearchSort(sort);
      } else {
        message.error('Search failed');
        setSearchResults([]);
      }
    } catch (error) {
      console.error('Error searching posts:', error);
      message.error('Search failed');
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  // 🆕 清除搜索
  const clearSearch = () => {
    setIsSearchMode(false);
    setSearchResults([]);
    setSearchQuery("");
  };

  // 🆕 加载收藏的帖子
  const loadBookmarkedPosts = async (page = 1, reset = false) => {
    try {
      setPostsLoading(true);
      const response = await squareService.getBookmarks(page, 20);

      if (response.success) {
        const newPosts = response.data.posts || [];
        if (reset) {
          setBookmarkedPostsList(newPosts);
        } else {
          setBookmarkedPostsList(prev => [...prev, ...newPosts]);
        }
      } else {
        console.error('Failed to load bookmarked posts:', response.error);
        message.error('Failed to load bookmarked posts');
      }
    } catch (error) {
      console.error('Error loading bookmarked posts:', error);
      message.error('Error loading bookmarked posts');
    } finally {
      setPostsLoading(false);
    }
  };

  // 🆕 获取当前用户头像和信息
  useEffect(() => {
    const loadCurrentUserInfo = async () => {
      if (!isAuthenticated || !user) {
        setCurrentUserAvatar(null);
        return;
      }

      try {
        console.log('Loading user info for Square page...');
        const userInfo = await authService.getUserInfo(true); // 强制刷新
        console.log('User info loaded:', userInfo);

        if (userInfo?.avatar_url) {
          setCurrentUserAvatar(userInfo.avatar_url);
          console.log('Avatar loaded:', userInfo.avatar_url);
        } else {
          setCurrentUserAvatar(null);
          console.log('No avatar found for user');
        }
      } catch (error) {
        console.warn('Failed to load user info:', error);
        setCurrentUserAvatar(null);
      }
    };

    loadCurrentUserInfo();
  }, [isAuthenticated, user]);

  // 🆕 初始化数据加载
  useEffect(() => {
    if (isAuthenticated) {
      loadPosts(1, true);
      loadStats();
      loadPopularTags();
    }
  }, [isAuthenticated]); // 只在认证状态改变时触发

  // 🆕 当时间线或排序方式改变时重新加载
  useEffect(() => {
    if (isAuthenticated) {
      if (timeline === 'bookmarks') {
        loadBookmarkedPosts(1, true);
      } else {
        loadPosts(1, true);
      }
    }
  }, [timeline, sortBy, isAuthenticated]); // 添加isAuthenticated依赖

  // Sort posts based on selected criteria
  const currentPosts = isSearchMode ? searchResults : (timeline === 'bookmarks' ? bookmarkedPostsList : posts);
  const sortedPosts = [...currentPosts].sort((a, b) => {
    if (isSearchMode) {
      // 搜索模式下使用搜索排序
      if (searchSort === 'time') {
        return new Date(b.timestamp) - new Date(a.timestamp);
      } else if (searchSort === 'replies') {
        return b.replies - a.replies;
      }
    } else {
      // 正常模式下使用常规排序
      if (sortBy === "time") {
        return new Date(b.timestamp) - new Date(a.timestamp);
      } else {
        // Sort by "hot" - combination of likes and replies
        const scoreA = a.likes + (a.replies * 2);
        const scoreB = b.likes + (b.replies * 2);
        return scoreB - scoreA;
      }
    }
    return 0;
  });

  // 🆕 发布帖子
  const handlePostSubmit = async () => {
    if (!newPost.trim()) {
      message.warning("Please enter some content");
      return;
    }

    // 验证标签
    if (postTags.length > 0) {
      const validation = squareService.validateTags(postTags);
      if (!validation.valid) {
        message.error(validation.error);
        return;
      }
    }

    try {
      setLoading(true);

      // 准备帖子数据
      const postData = {
        content: newPost,
        image: selectedImage, // 如果有图片，这里应该是base64或URL
        tags: postTags.length > 0 ? squareService.formatTags(postTags) : null
      };

      const response = await squareService.createPost(postData);

      if (response.success) {
        // 将新帖子添加到列表顶部
        setPosts(prev => [response.data.post, ...prev]);
        setNewPost("");
        setSelectedImage(null);
        setPostTags([]);
        message.success("Post published successfully!");
      } else {
        message.error(response.error?.message || "Failed to publish post");
      }
    } catch (error) {
      console.error('Error publishing post:', error);
      message.error("Failed to publish post");
    } finally {
      setLoading(false);
    }
  };

  // 🆕 图片上传处理
  const handleImageUpload = async (file) => {
    try {
      // 验证文件
      if (file.size > 2 * 1024 * 1024) {
        message.error("Image size must be less than 2MB");
        return false;
      }

      if (!file.type.includes("png")) {
        message.error("Only PNG images are supported");
        return false;
      }

      // 方法1: 上传到服务器获取URL
      try {
        const response = await squareService.uploadImage(file);
        if (response.success) {
          setSelectedImage(response.data.url);
          message.success("Image uploaded successfully!");
        } else {
          throw new Error(response.error?.message || "Upload failed");
        }
      } catch (uploadError) {
        console.warn("Server upload failed, using base64 fallback:", uploadError);

        // 方法2: 转换为base64作为备用方案
        const base64 = await squareService.fileToBase64(file);
        setSelectedImage(base64);
        message.success("Image prepared for upload!");
      }

      return false; // 阻止antd的默认上传行为
    } catch (error) {
      console.error('Error handling image upload:', error);
      message.error("Failed to process image");
      return false;
    }
  };

  const handleReply = (post) => {
    setSelectedPost(post);
    setReplyModalVisible(true);
  };

  // 🆕 提交回复
  const submitReply = async () => {
    if (!replyText.trim()) {
      message.warning("Please enter reply content");
      return;
    }

    try {
      setLoading(true);

      const response = await squareService.createReply(selectedPost.id, replyText);

      if (response.success) {
        // 更新本地帖子数据
        const updatedPosts = posts.map(post => {
          if (post.id === selectedPost.id) {
            return {
              ...post,
              replies: post.replies + 1,
              replies_list: [...(post.replies_list || []), response.data.reply]
            };
          }
          return post;
        });

        setPosts(updatedPosts);
        setReplyText("");
        setReplyModalVisible(false);
        message.success("Reply posted successfully!");
      } else {
        message.error(response.error?.message || "Failed to post reply");
      }
    } catch (error) {
      console.error('Error posting reply:', error);
      message.error("Failed to post reply");
    } finally {
      setLoading(false);
    }
  };

  // 🆕 修改：展开回复时自动加载回复列表
  const toggleReplies = async (postId) => {
    const isCurrentlyExpanded = expandedReplies[postId];

    // 切换展开状态
    setExpandedReplies(prev => ({
      ...prev,
      [postId]: !prev[postId]
    }));

    // 如果是展开操作，且回复列表未加载，则从 API 加载
    if (!isCurrentlyExpanded) {
      const post = posts.find(p => p.id === postId);

      // 如果帖子有回复但回复列表为空，则加载回复
      if (post && post.replies > 0 && (!post.replies_list || post.replies_list.length === 0)) {
        try {
          const response = await squareService.getReplies(postId);

          if (response.success && response.data.replies) {
            // 更新帖子的回复列表
            const updatedPosts = posts.map(p => {
              if (p.id === postId) {
                return {
                  ...p,
                  replies_list: response.data.replies
                };
              }
              return p;
            });

            setPosts(updatedPosts);
          }
        } catch (error) {
          console.error('Error loading replies:', error);
          message.error('Failed to load replies');
        }
      }
    }
  };

  // 🆕 处理点赞
  const handleLike = async (postId) => {
    const isCurrentlyLiked = likedPosts.has(postId);

    try {
      // 乐观更新UI
      setLikedPosts(prev => {
        const newSet = new Set(prev);
        if (isCurrentlyLiked) {
          newSet.delete(postId);
        } else {
          newSet.add(postId);
        }
        return newSet;
      });

      // 更新帖子点赞数
      const updatedPosts = posts.map(post => {
        if (post.id === postId) {
          return {
            ...post,
            likes: isCurrentlyLiked ? post.likes - 1 : post.likes + 1,
            is_liked: !isCurrentlyLiked
          };
        }
        return post;
      });
      setPosts(updatedPosts);

      // 调用API
      const response = isCurrentlyLiked
        ? await squareService.unlikePost(postId)
        : await squareService.likePost(postId);

      if (!response.success) {
        // 如果API调用失败，回滚UI更改
        setLikedPosts(prev => {
          const newSet = new Set(prev);
          if (isCurrentlyLiked) {
            newSet.add(postId);
          } else {
            newSet.delete(postId);
          }
          return newSet;
        });

        const revertedPosts = posts.map(post => {
          if (post.id === postId) {
            return {
              ...post,
              likes: isCurrentlyLiked ? post.likes + 1 : post.likes - 1,
              is_liked: isCurrentlyLiked
            };
          }
          return post;
        });
        setPosts(revertedPosts);

        message.error("Failed to update like status");
      } else {
        // 使用服务器返回的准确数据更新
        const finalPosts = posts.map(post => {
          if (post.id === postId) {
            return {
              ...post,
              likes: response.data.likes_count,
              is_liked: response.data.is_liked
            };
          }
          return post;
        });
        setPosts(finalPosts);
      }
    } catch (error) {
      console.error('Error handling like:', error);
      // 回滚UI更改
      setLikedPosts(prev => {
        const newSet = new Set(prev);
        if (isCurrentlyLiked) {
          newSet.add(postId);
        } else {
          newSet.delete(postId);
        }
        return newSet;
      });
      message.error("Failed to update like status");
    }
  };

  // 🆕 处理关注
  const handleFollow = async (userId) => {
    const isCurrentlyFollowing = followedUsers.has(userId);

    try {
      // 乐观更新UI
      setFollowedUsers(prev => {
        const newSet = new Set(prev);
        if (isCurrentlyFollowing) {
          newSet.delete(userId);
        } else {
          newSet.add(userId);
        }
        return newSet;
      });

      // 调用API
      const response = isCurrentlyFollowing
        ? await squareService.unfollowUser(userId)
        : await squareService.followUser(userId);

      if (response.success) {
        message.success(isCurrentlyFollowing ? "Unfollowed user!" : "Following user!");
      } else {
        // 回滚UI更改
        setFollowedUsers(prev => {
          const newSet = new Set(prev);
          if (isCurrentlyFollowing) {
            newSet.add(userId);
          } else {
            newSet.delete(userId);
          }
          return newSet;
        });
        message.error("Failed to update follow status");
      }
    } catch (error) {
      console.error('Error handling follow:', error);
      // 回滚UI更改
      setFollowedUsers(prev => {
        const newSet = new Set(prev);
        if (isCurrentlyFollowing) {
          newSet.add(userId);
        } else {
          newSet.delete(userId);
        }
        return newSet;
      });
      message.error("Failed to update follow status");
    }
  };

  // 🆕 处理收藏
  const handleBookmark = async (postId) => {
    const isCurrentlyBookmarked = bookmarkedPosts.has(postId);

    try {
      // 乐观更新UI
      setBookmarkedPosts(prev => {
        const newSet = new Set(prev);
        if (isCurrentlyBookmarked) {
          newSet.delete(postId);
        } else {
          newSet.add(postId);
        }
        return newSet;
      });

      // 调用API
      const response = isCurrentlyBookmarked
        ? await squareService.unbookmarkPost(postId)
        : await squareService.bookmarkPost(postId);

      if (response.success) {
        message.success(isCurrentlyBookmarked ? "Removed from bookmarks!" : "Post bookmarked!");

        // 更新帖子的收藏状态
        const updatedPosts = posts.map(post => {
          if (post.id === postId) {
            return {
              ...post,
              is_bookmarked: response.data.is_bookmarked
            };
          }
          return post;
        });
        setPosts(updatedPosts);
      } else {
        // 回滚UI更改
        setBookmarkedPosts(prev => {
          const newSet = new Set(prev);
          if (isCurrentlyBookmarked) {
            newSet.add(postId);
          } else {
            newSet.delete(postId);
          }
          return newSet;
        });
        message.error("Failed to update bookmark status");
      }
    } catch (error) {
      console.error('Error handling bookmark:', error);
      // 回滚UI更改
      setBookmarkedPosts(prev => {
        const newSet = new Set(prev);
        if (isCurrentlyBookmarked) {
          newSet.add(postId);
        } else {
          newSet.delete(postId);
        }
        return newSet;
      });
      message.error("Failed to update bookmark status");
    }
  };

  // 🆕 使用squareService的formatTime方法
  const formatTime = (timestamp) => {
    return squareService.formatTime(timestamp);
  };

  // Render login required state (similar to Settings page)
  if (!isAuthenticated) {
    return (
      <Layout>
        <div className={`search-page light-theme`}>
          <div className="hero-section1">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="hero-content"
            >
              <Title level={1} className="hero-title" style={{ color: "#fff" }}>
                Square Community
              </Title>
              <Paragraph className="hero-subtitle">
                Connect with researchers, share discoveries, and engage in scientific discussions.
              </Paragraph>
            </motion.div>
          </div>
          <div
            style={{
              padding: "2rem",
              maxWidth: "1200px",
              margin: "0 auto",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <div
              className="scholar-container"
              style={{
                width: "100%",
                padding: "2rem",
              }}
            >
              <div style={{ textAlign: "center" }}>
                <UserOutlined
                  style={{
                    fontSize: 64,
                    color: "#fff",
                    marginBottom: 7,
                  }}
                />
                <Title
                  level={2}
                  style={{
                    color: "#fff",
                    marginBottom: 16,
                  }}
                >
                  Login Required
                </Title>
                <Text
                  style={{
                    color: "#ccc",
                    fontSize: 16,
                    marginBottom: 24,
                    maxWidth: 500,
                    margin: "0 auto 24px",
                    display: "block",
                  }}
                >
                  Please sign in to join the scientific community, share your research, and engage with fellow researchers.
                </Text>

                <Button
                  className="modern-btn modern-btn-primary"
                  type="primary"
                  size="large"
                  style={{
                    height: 48,
                    paddingLeft: 32,
                    paddingRight: 32,
                    fontSize: 16,
                  }}
                  onClick={() => {
                    const loginButton = document.querySelector(".login-btn");
                    if (loginButton) {
                      loginButton.click();
                    } else {
                      message.info("Please use the login button in the top right corner to log in");
                    }
                  }}
                >
                  Sign In to Continue
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className={`search-page light-theme`}>
        <div className="hero-section1">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="hero-content"
          >
            <Title level={1} className="hero-title" style={{ color: "#fff" }}>
              Square Community
            </Title>
            <Paragraph className="hero-subtitle">
              Share discoveries, connect with researchers, and engage in scientific discussions.
            </Paragraph>
          </motion.div>
        </div>

        <div
          style={{
            padding: "2rem",
            maxWidth: "1400px",
            margin: "0 auto",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <div
            className="scholar-container"
            style={{
              width: "100%",
              padding: "2rem",
            }}
          >


            {/* Post Creation */}
            <Card className="post-creation-card" style={{ marginBottom: '1.5rem' }}>
              <div className="post-creation">
                <Avatar
                  src={currentUserAvatar || "https://api.dicebear.com/7.x/avataaars/svg?seed=User"}
                  size={48}
                  style={{ marginRight: '1rem' }}
                />
                <div className="post-input-area">
                  <TextArea
                    placeholder="Share your research insights, discoveries, or thoughts..."
                    value={newPost}
                    onChange={(e) => setNewPost(e.target.value)}
                    rows={4}
                    style={{ marginBottom: '1rem' }}
                  />

                  {/* 标签输入 */}
                  <div style={{ marginBottom: '1rem' }}>
                    <Typography.Text style={{ display: 'block', marginBottom: '0.5rem', color: '#666' }}>
                      Tags (optional, max 5)
                    </Typography.Text>
                    <Select
                      mode="tags"
                      style={{ width: '100%' }}
                      placeholder="Enter tags like research, AI, science..."
                      value={postTags}
                      onChange={(tags) => {
                        if (tags.length <= 5) {
                          setPostTags(tags);
                        } else {
                          message.warning('Maximum 5 tags allowed');
                          setPostTags(tags.slice(0, 5));
                        }
                      }}
                      tokenSeparators={[',', ' ', '#']}
                      maxTagCount={5}
                    />
                    {postTags.length > 0 && (
                      <div style={{ marginTop: '0.5rem' }}>
                        <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
                          Tags ({postTags.length}/5)
                        </Typography.Text>
                      </div>
                    )}
                  </div>

                  {selectedImage && (
                    <div className="selected-image">
                      <img src={selectedImage} alt="Selected" style={{ maxWidth: '200px', borderRadius: '8px' }} />
                      <Button
                        type="text"
                        onClick={() => setSelectedImage(null)}
                        style={{ position: 'absolute', top: 0, right: 0 }}
                      >
                        ×
                      </Button>
                    </div>
                  )}
                  <div className="post-actions">

                    <Space>
                      <Upload
                        beforeUpload={handleImageUpload}
                        showUploadList={false}
                        accept=".png"
                      >
                        <Button icon={<PictureOutlined />} type="text">
                          Add Image (PNG, &lt;2MB)
                        </Button>
                      </Upload>
                    </Space>
                    <Button
                      type="primary"
                      icon={loading ? <LoadingOutlined /> : <SendOutlined />}
                      onClick={handlePostSubmit}
                      disabled={!newPost.trim() || loading}
                      loading={loading}
                    >
                      {loading ? "Publishing..." : "Post"}
                    </Button>
                  </div>
                </div>
              </div>
            </Card>



            {/* Posts Feed */}
            <div className="posts-feed">

              {/* 🆕 搜索栏 */}
              <Card style={{ marginBottom: '1.5rem' }}>
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                  <Input.Search
                    placeholder="Search posts by content or tags..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onSearch={(value) => handleSearch(value, searchSort)}
                    loading={searchLoading}
                    style={{ flex: 1 }}
                    size="large"
                  />
                  {isSearchMode && (
                    <>
                      <Select
                        value={searchSort}
                        onChange={(value) => {
                          setSearchSort(value);
                          if (searchQuery) {
                            handleSearch(searchQuery, value);
                          }
                        }}
                        style={{ width: 120 }}
                        size="large"
                      >
                        <Select.Option value="time">Latest</Select.Option>
                        <Select.Option value="replies">Most Replies</Select.Option>
                      </Select>
                      <Button onClick={clearSearch} size="large">
                        Clear
                      </Button>
                    </>
                  )}
                </div>
                {isSearchMode && (
                  <div style={{ marginTop: '1rem', fontSize: '14px', color: '#666' }}>
                    {searchLoading ? (
                      <Spin size="small" style={{ marginRight: '0.5rem' }} />
                    ) : (
                      `Found ${searchResults.length} result(s) for "${searchQuery}"`
                    )}
                  </div>
                )}
              </Card>
              {/* Timeline and Sort Controls */}
              <Card style={{ marginBottom: '1.5rem' }}>
                <div className="timeline-controls">
                  <Tabs
                    activeKey={timeline}
                    onChange={setTimeline}
                    tabBarExtraContent={
                      <Space>
                        <Button
                          type={sortBy === 'time' ? 'primary' : 'default'}
                          icon={<ClockCircleOutlined />}
                          onClick={() => setSortBy('time')}
                        >
                          Latest
                        </Button>
                        <Button
                          type={sortBy === 'hot' ? 'primary' : 'default'}
                          icon={<FireOutlined />}
                          onClick={() => setSortBy('hot')}
                        >
                          Popular
                        </Button>
                      </Space>
                    }
                  >
                    <Tabs.TabPane tab="Wonder" key="discover" />
                    <Tabs.TabPane tab="Follow" key="following" />
                    <Tabs.TabPane tab="Bookmarks" key="bookmarks" />
                  </Tabs>
                </div>
              </Card>

              {/* 🆕 加载状态 */}
              {postsLoading && posts.length === 0 ? (
                <Card style={{ textAlign: 'center', padding: '2rem' }}>
                  <Spin size="large" tip="Loading posts..." />
                </Card>
              ) : sortedPosts.length === 0 ? (
                <Card style={{ textAlign: 'center', padding: '2rem' }}>
                  <Text type="secondary">No posts found. Be the first to share something!</Text>
                </Card>
              ) : (
                sortedPosts.map((post) => (
                  <Card key={post.id} className="post-card" style={{ marginBottom: '1.5rem' }}>
                    <div className="post-header">
                      <Avatar src={post.user.avatar} size={40} />
                      <div className="post-user-info">
                        <Space>
                          <Text strong>{post.user.name}</Text>
                          {post.user.verified && <Tag color="blue">Verified</Tag>}
                        </Space>
                        <Text type="secondary">{formatTime(post.timestamp)}</Text>
                      </div>
                      <div className="post-header-actions">
                        <Space>
                          {/* 只有当帖子作者不是当前用户时才显示关注按钮 */}
                          {post.user.id !== user?.user_id && (
                            <Button
                              type={followedUsers.has(post.user.id) ? "primary" : "text"}
                              icon={<UserAddOutlined />}
                              size="small"
                              onClick={() => handleFollow(post.user.id)}
                            >
                              {followedUsers.has(post.user.id) ? "Following" : "Follow"}
                            </Button>
                          )}
                          <Button
                            type={bookmarkedPosts.has(post.id) ? "primary" : "text"}
                            icon={<StarOutlined />}
                            size="small"
                            onClick={() => handleBookmark(post.id)}
                          >
                            {bookmarkedPosts.has(post.id) ? "Bookmarked" : "Bookmark"}
                          </Button>
                        </Space>
                      </div>
                    </div>

                    <div className="post-content">
                      <ReactMarkdown>{post.content}</ReactMarkdown>
                      {post.image && (
                        <img
                          src={post.image}
                          alt="Post content"
                          className="post-image"
                          style={{ maxWidth: '100%', borderRadius: '8px', marginTop: '1rem' }}
                        />
                      )}
                    </div>

                    {/* 🆕 标签显示 */}
                    {post.tags && post.tags.length > 0 && (
                      <div className="post-tags" style={{ margin: '1rem 0' }}>
                        {post.tags.map((tag, index) => (
                          <Tag
                            key={index}
                            color="blue"
                            style={{ marginBottom: '0.25rem', cursor: 'pointer' }}
                            onClick={() => handleSearch(tag, 'time')}
                          >
                            #{tag}
                          </Tag>
                        ))}
                      </div>
                    )}

                    <div className="post-actions">
                      <Space size="large">
                        <Button
                          type="text"
                          icon={<HeartOutlined />}
                          onClick={() => handleLike(post.id)}
                          style={{
                            color: likedPosts.has(post.id) ? '#ff4757' : undefined
                          }}
                        >
                          {post.likes}
                        </Button>
                        {/* 🆕 修改：点击展开/收起回复列表，而不是直接打开回复弹窗 */}
                        <Button
                          type="text"
                          icon={<MessageOutlined />}
                          onClick={() => toggleReplies(post.id)}
                          style={{
                            color: expandedReplies[post.id] ? '#1890ff' : undefined
                          }}
                        >
                          {post.replies} {expandedReplies[post.id] ? '▲' : '▼'}
                        </Button>
                        <Button type="text" icon={<RetweetOutlined />}>
                          {post.reposts}
                        </Button>
                      </Space>
                    </div>

                    {/* 🆕 修改：回复区域 - 始终显示回复按钮，即使没有回复 */}
                    {expandedReplies[post.id] && (
                      <div className="replies-section" style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #f0f0f0' }}>
                        {/* 回复列表 */}
                        {post.replies_list && post.replies_list.length > 0 ? (
                          <>
                            {post.replies_list.map((reply) => (
                              <div key={reply.id} className="reply-item" style={{ marginBottom: '1rem', paddingLeft: '1rem' }}>
                                <div className="reply-header">
                                  <Avatar src={reply.user.avatar} size={32} />
                                  <div style={{ marginLeft: '0.5rem' }}>
                                    <Text strong>{reply.user.name}</Text>
                                    <Text type="secondary" style={{ marginLeft: '0.5rem' }}>
                                      {formatTime(reply.timestamp)}
                                    </Text>
                                  </div>
                                </div>
                                <div className="reply-content" style={{ marginTop: '0.5rem', marginLeft: '2.5rem' }}>
                                  <ReactMarkdown>{reply.content}</ReactMarkdown>
                                </div>
                              </div>
                            ))}
                          </>
                        ) : (
                          <div style={{ textAlign: 'center', padding: '1rem', color: '#999' }}>
                            <Text type="secondary">No replies yet. Be the first to reply!</Text>
                          </div>
                        )}

                        {/* 🆕 回复按钮 - 始终显示 */}
                        <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #f5f5f5' }}>
                          <Button
                            type="primary"
                            size="medium"
                            icon={<MessageOutlined />}
                            onClick={() => handleReply(post)}
                          >
                            Write a Reply
                          </Button>
                        </div>
                      </div>
                    )}
                  </Card>
                ))
              )}
            </div>

            {/* 🆕 热门标签 */}
            {/* <Card
              className="sidebar-card"
              title={
                <span>
                  <Tag style={{ marginRight: '0.5rem', color: '#1890ff' }} />
                  Popular Tags
                </span>
              }
              style={{ marginTop: '1.5rem' }}
            >
              <div className="popular-tags">
                {popularTags.length > 0 ? (
                  popularTags.slice(0, 10).map((tag, index) => (
                    <Tag
                      key={index}
                      color="blue"
                      style={{
                        marginBottom: '0.5rem',
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.25rem'
                      }}
                      onClick={() => handleSearch(tag.name, 'time')}
                    >
                      #{tag.name}
                      <span style={{ fontSize: '0.8em', opacity: 0.7 }}>
                        ({tag.count})
                      </span>
                    </Tag>
                  ))
                ) : (
                  <Text type="secondary">No popular tags available</Text>
                )}
              </div>
            </Card> */}

            {/* Trending Topics */}
            {/* <Card
              className="sidebar-card"
              title={
                <span>
                  <GlobalOutlined style={{ marginRight: '0.5rem', color: '#52c41a' }} />
                  Trending Topics
                </span>
              }
              style={{ marginTop: '1.5rem' }}
            >
              <div className="trending-topics">
                {statsLoading ? (
                  <div style={{ textAlign: 'center', padding: '1rem' }}>
                    <Spin size="small" tip="Loading trends..." />
                  </div>
                ) : stats.trending && stats.trending.length > 0 ? (
                  stats.trending.map((topic, index) => (
                    <Tag key={index} color="blue" style={{ marginBottom: '0.5rem' }}>
                      {topic}
                    </Tag>
                  ))
                ) : (
                  <Text type="secondary">No trending topics available</Text>
                )}
              </div>
            </Card> */}
          </div>
        </div>
      </div>

      {/* Reply Modal */}
      <Modal
        title="Reply to Post"
        open={replyModalVisible}
        onCancel={() => setReplyModalVisible(false)}
        onOk={submitReply}
        okText={loading ? "Replying..." : "Reply"}
        confirmLoading={loading}
        okButtonProps={{ disabled: !replyText.trim() || loading }}
      >
        {selectedPost && (
          <div>
            <div className="original-post" style={{ marginBottom: '1rem', padding: '1rem', background: '#f5f5f5', borderRadius: '8px' }}>
              <Space>
                <Avatar src={selectedPost.user.avatar} size={32} />
                <Text strong>{selectedPost.user.name}</Text>
              </Space>
              <div style={{ marginTop: '0.5rem' }}>
                <ReactMarkdown>{selectedPost.content}</ReactMarkdown>
              </div>
            </div>
            <TextArea
              placeholder="Write your reply..."
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              rows={4}
            />
          </div>
        )}
      </Modal>
    </Layout>
  );
};

export default SquarePage;
